import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Shield,
  Truck,
  CreditCard,
  CheckCircle,
  Search,
  RefreshCw,
  Calculator,
} from "lucide-react";
import { useState } from "react";
import { Button, Card, CardContent } from "@/components/ui";
import { VehicleCard } from "@/components/vehicles";
import { getFeaturedVehicles, getVehicleSummaries } from "@/data/vehicles";
import { getBodyTypes } from "@/data/shop";
import { useSeo } from "@/lib/seo";

function BodyTypeCardImage({ slug, label }: { slug: string; label: string }) {
  const [failed, setFailed] = useState(false);
  const jpgSrc = `/shop-images/body-types/${slug}.jpg`;
  const webpSrc = `/shop-images/body-types/${slug}.webp`;
  if (failed) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-6xl font-headline font-light text-navy-900/10 group-hover:scale-110 transition-transform duration-500">
          {label.charAt(0)}
        </span>
      </div>
    );
  }
  return (
    <picture>
      <source srcSet={webpSrc} type="image/webp" />
      <img
        src={jpgSrc}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
    </picture>
  );
}

const features = [
  {
    icon: Shield,
    title: "Certified Vehicles",
    description: "Every vehicle undergoes a rigorous multi-point inspection.",
    imageSlug: "certified-vehicles",
  },
  {
    icon: Truck,
    title: "Free Delivery",
    description: "We deliver your vehicle right to your doorstep.",
    imageSlug: "free-delivery",
  },
  {
    icon: CreditCard,
    title: "Easy Financing",
    description: "Get financing approval in minutes, not days.",
    imageSlug: "easy-financing",
  },
  {
    icon: CheckCircle,
    title: "7-Day Return",
    description: "Not satisfied? Return it within 7 days, no questions asked.",
    imageSlug: "7-day-return",
  },
];

const quickActions = [
  {
    icon: Search,
    title: "Search Inventory",
    description: "Find your ideal vehicle",
    href: "/vehicles",
    imageSlug: "search-inventory",
  },
  {
    icon: RefreshCw,
    title: "Trade-In Value",
    description: "Get an instant estimate",
    href: "/trade-in",
    imageSlug: "trade-in-value",
  },
  {
    icon: Calculator,
    title: "Payment Calculator",
    description: "Estimate monthly payments",
    href: "/financing",
    imageSlug: "payment-calculator",
  },
];

export function HomePage() {
  const navigate = useNavigate();
  const [heroQuery, setHeroQuery] = useState("");
  const featuredVehicles = getFeaturedVehicles();
  const bodyTypes = getBodyTypes();
  const availableCount = getVehicleSummaries().filter(
    (v) => v.status === "available" || v.status === "pending",
  ).length;

  useSeo({
    title: "BESPOKIAN | Auto",
    description: "Bespokian Auto - Premium Automotive E-Commerce Platform",
    // Use the dedicated OG-safe share image (1200x630 jpg)
    image: "/img/og/home-og.jpg",
  });

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('/img/heroes/home-hero-hero.webp')` }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/35" />

        {/* Content - centered in viewport */}
        <div className="relative z-10 w-full flex flex-col items-center justify-center px-6">
          <div className="text-center max-w-4xl w-full">
            <h1 className="font-display text-5xl md:text-6xl text-white leading-tight tracking-tight">
              Find Your Perfect Vehicle
            </h1>
          </div>

          {/* Search (match BrandsPage hero glass panel) */}
          <div className="mt-10 w-full max-w-2xl">
            <form
              className="w-full"
              onSubmit={(e) => {
                e.preventDefault();
                const q = heroQuery.trim();
                navigate(
                  q ? `/vehicles?q=${encodeURIComponent(q)}` : "/vehicles",
                );
              }}
            >
              <div className="w-full">
                <div
                  className="backdrop-blur-md rounded-lg px-6 py-4 shadow-xl w-full border border-white/20"
                  style={{ backgroundColor: "rgba(0, 0, 0, 0.21)" }}
                >
                  <label
                    htmlFor="home-hero-search"
                    className="block text-xs uppercase tracking-widest text-white mb-3 text-left font-medium"
                  >
                    Which vehicle are you looking for?
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      id="home-hero-search"
                      type="text"
                      placeholder="Search make, model, or trim..."
                      value={heroQuery}
                      onChange={(e) => setHeroQuery(e.target.value)}
                      className="flex-1 text-base text-white placeholder:text-white/70 bg-transparent border-none outline-none focus:outline-none"
                    />
                    <button
                      type="submit"
                      className="text-white hover:text-white/80 transition-colors p-2 hover:bg-white/10 rounded-md"
                      aria-label="Search vehicles"
                    >
                      <Search className="w-6 h-6" />
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/vehicles" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="bg-white text-navy-900 hover:bg-white/90 w-full"
              >
                Browse Vehicles
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/financing" className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10 w-full"
              >
                Calculate Payment
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="border-b border-gray-200 bg-white py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {quickActions.map((action) => (
              <Link
                key={action.title}
                to={action.href}
                className="group relative overflow-hidden flex items-center gap-4 rounded-lg border border-gray-200 p-4 transition-all hover:border-primary-300 hover:shadow-sm"
              >
                {/* Background image for the whole link */}
                <div className="absolute inset-0" aria-hidden>
                  <picture>
                    <source
                      srcSet={`/home-images/quick-actions/${action.imageSlug}.webp`}
                      type="image/webp"
                    />
                    <img
                      src={`/home-images/quick-actions/${action.imageSlug}.jpg`}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </picture>
                  {/* Keep text readable, but let image show */}
                  <div className="absolute inset-0 bg-white/70 group-hover:bg-white/60 transition-colors" />
                  <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-white/65 to-white/35" />
                </div>

                <div className="relative z-10 flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600 transition-colors group-hover:bg-primary-200">
                    <action.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {action.title}
                    </h3>
                    <p className="text-sm text-gray-700">
                      {action.description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Vehicles */}
      <section className="py-section-lg px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Featured Vehicles
            </h2>
            <p className="mt-3 text-lg text-gray-600">
              Browse our selection of {availableCount}+ quality pre-owned
              vehicles. Get instant financing quotes, trade-in estimates, and
              apply online today.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredVehicles.map((vehicle) => (
              <VehicleCard key={vehicle.id} vehicle={vehicle} />
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/vehicles">
              <Button size="lg">
                View All {availableCount} Vehicles
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Shop by Body Type */}
      <section className="py-section-lg px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Shop by Body Type
            </h2>
            <p className="mt-3 text-lg text-gray-600">
              These collection pages come from the client’s supplied shop
              dataset.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {bodyTypes.map((bt) => (
              <Link
                key={bt.slug}
                to={`/shop/${bt.slug}`}
                className="group relative bg-white rounded-2xl overflow-hidden border border-line-gray hover:border-navy-900/20 transition-all duration-300 hover:shadow-xl"
              >
                {/* Image placeholder (room for incoming images) */}
                <div className="aspect-[4/3] bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/40 via-navy-900/10 to-transparent" />
                  <BodyTypeCardImage slug={bt.slug} label={bt.pluralName} />
                </div>

                <div className="p-6">
                  <h3 className="font-headline text-2xl font-light text-navy-900 group-hover:text-primary-600 transition-colors capitalize">
                    {bt.pluralName}
                  </h3>
                  <p className="mt-1 text-sm font-medium tracking-wide text-muted-navy uppercase">
                    {bt.vehicleCount.toLocaleString()} vehicles
                  </p>
                  <div className="mt-6 flex items-center text-sm font-medium text-navy-900 group-hover:text-primary-600 transition-colors">
                    <span>Explore</span>
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/shop">
              <Button size="lg" variant="outline">
                Explore all shop pages
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Why Choose Bespokian Auto?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <Card
                key={feature.title}
                className="text-center border-0 shadow-sm relative overflow-hidden rounded-xl"
                style={{
                  backgroundImage: `url('/home-images/features/${feature.imageSlug}.jpg')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                {/* Prefer WebP as a progressive enhancement for capable browsers */}
                <picture className="sr-only">
                  <source
                    srcSet={`/home-images/features/${feature.imageSlug}.webp`}
                    type="image/webp"
                  />
                  <img
                    src={`/home-images/features/${feature.imageSlug}.jpg`}
                    alt=""
                  />
                </picture>

                {/* Readability overlays (must cover full card, not just content area) */}
                <div className="absolute inset-0 bg-black/55" aria-hidden />
                <div
                  className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/55 to-black/70"
                  aria-hidden
                />

                <CardContent className="pt-6 relative z-10">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/20 backdrop-blur-sm">
                    <feature.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-white/85 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-primary-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold">
            Ready to Find Your Next Vehicle?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-primary-100">
            Start your search today and drive away in the car of your dreams.
            Our team is here to help every step of the way.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/vehicles">
              <Button
                size="lg"
                className="bg-white text-primary-600 hover:bg-primary-50 w-full sm:w-auto"
              >
                Browse Inventory
              </Button>
            </Link>
            <Link to="/financing">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 w-full sm:w-auto"
              >
                Get Pre-Approved
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
